<template>
  <div id="thank-you-page" class="container">
    <div class="row mt-4">
      <div class="col-10 mx-auto">
        <!-- Header -->
        <div class="row mt-4 mb-4">
          <div class="col-12 desktop-heading-m">
            Thank You for Voting!
          </div>
        </div>

        <!-- Confirmation Message -->
        <div class="confirmation-message" v-if="!isMobile">
          <p>This data helps us understand the impact of climate on our built environment and has improved our ability to design for environmental performance with greater insight.</p>
          <!-- <p>As a thank you token, we've created a augmented reality filter to share on your social media. Make sure to grant access of your camera and location while you're using it</p> -->
        </div>

        <!-- Vote Details -->
        <div class="vote-details" v-if="voteData">
          <div class="details-card">
            <div class="details-grid" :class="{ 'mobile-layout': isMobile }">
              <!-- Left Column: Weather Icon -->
              <div class="weather-column">
                <div id="weather-icon">{{ getWeatherIcon(voteData.weather_condition) }}</div>
                <div class="weather-description" v-if="!isMobile">{{ voteData.weather_condition }}</div>
              </div>
              
              <!-- Right Column: Vote Details -->
              <div class="details-column">
                <div class="detail-item">
                  <span class="label">Email:</span>
                  <span class="value">{{ voteData.user_email }}</span>
                </div>
                <div class="detail-item">
                  <span class="label">Comfort Level:</span>
                  <span class="value">{{ formatComfortLevel(voteData.comfort_vote_result) }}</span>
                </div>
                <div class="detail-item" v-if="voteData.clothing_level != null">
                  <span class="label">Clothing Level:</span>
                  <span class="value">{{ formatClothingLevel(voteData.clothing_level) }}</span>
                </div>
                <div class="detail-item" v-if="voteData.activity_level != null">
                  <span class="label">Activity Level:</span>
                  <span class="value">{{ formatActivityLevel(voteData.activity_level) }}</span>
                </div>
                <div class="detail-item">
                  <span class="label">Location:</span>
                  <span class="value">{{ voteData.coordinate.x.toFixed(4) }}, {{ voteData.coordinate.y.toFixed(4) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Action Buttons and QR Code Container (hidden) -->
        <div v-if="false" class="action-container">
          <!-- Action Buttons -->
          <div class="action-buttons">
            <button @click="submitAnotherVote" class="action-btn submit-another">
              Submit Another Vote
            </button>
            <button v-if="isMobile" @click="openAR" class="action-btn ar-btn">
              <span class="camera-icon">📷</span> Augmented Reality
            </button>
          </div>

          <!-- QR Code for Desktop -->
          <div v-if="!isMobile" class="qr-code-container">
            <img src="@/assets/comfortVotingAR.png" alt="AR QR Code" class="qr-code" />
            <p class="qr-instruction">Scan QR code to access AR filter</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { WEATHER_ICONS } from '@/assets/constants/weatherIcons';
import { isNightInNYC } from '@/utils/nycTime';

export default {
  name: "ThankYouPage",
  props: {
    voteData: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      weatherIcons: WEATHER_ICONS
    }
  },
  computed: {
    ...mapGetters({
      'mobileFlag': "getMobileFlag"
    }),
    isMobile() {
      return this.mobileFlag;
    }
  },
  methods: {
    formatComfortLevel(level) {
      const levels = {
        1: 'Too Cold',
        2: 'Cold',
        3: 'Comfortable',
        4: 'Hot',
        5: 'Too Hot'
      };
      return levels[level] || level;
    },
    formatClothingLevel(level) {
      const levels = {
        1: 'Heavy Coat',
        2: 'Light Jacket',
        3: 'Just Shirt'
      };
      return levels[level] != null ? levels[level] : level;
    },
    formatActivityLevel(level) {
      const levels = {
        1: 'Sitting',
        2: 'Standing',
        3: 'Slow Walk',
        4: 'Brisk Walk'
      };
      return levels[level] != null ? levels[level] : level;
    },
    formatTime(timestamp) {
      const date = new Date(timestamp);
      return date.toLocaleString();
    },
    getWeatherIcon(description) {
      const desc = (description || '').toLowerCase();
      const iconKey =
        desc === 'clear sky' && isNightInNYC() ? 'clear sky (night)' : desc;
      return this.weatherIcons[iconKey] || this.weatherIcons['default'];
    },
    submitAnotherVote() {
      this.$emit('submit-another');
    },
    returnToMap() {
      this.$emit('return-to-map');
    },
    openAR() {
      window.open('https://comfort-app-voting-ar.vercel.app/', '_blank');
    }
  }
}
</script>

<style lang="scss" scoped>
#thank-you-page {
  .desktop-heading-m {
    font-size: 2rem;
    font-weight: bold;
    color: white;
    text-align: center;
    margin-bottom: 2rem;
  }
}

.confirmation-message {
  text-align: center;
  margin: 2rem 0;
  
  .checkmark {
    font-size: 4rem;
    color: #4CAF50;
    margin-bottom: 1rem;
  }
  
  h2 {
    color: white;
    margin-bottom: 1rem;
  }
  
  p {
    color: #ccc;
    font-size: 1.2rem;
  }
}

.vote-details {
  margin: 2rem 0;
  
  .details-card {
    background-color: rgba(0, 0, 0, 0.5);
    border-radius: 8px;
    padding: 1.5rem;
  }
  
  .details-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 2rem;
    align-items: center;

    &.mobile-layout {
      grid-template-columns: 1fr;
      gap: 1rem;

      .weather-column {
        padding: 0.5rem;
        text-align: center;
        
        #weather-icon {
          font-size: 3rem;
          margin-bottom: 0.5rem;
        }
      }

      .details-column {
        gap: 0.75rem;
        text-align: center;

        .detail-item {
          .label {
            text-align: center;
          }
          
          .value {
            text-align: center;
          }
        }
      }
    }
  }
  
  .weather-column {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 1rem;
    
    #weather-icon {
      font-size: 4rem;
      margin-bottom: 1rem;
    }
    
    .weather-description {
      color: white;
      font-size: 1.2rem;
      text-transform: capitalize;
      text-align: center;
    }
  }
  
  .details-column {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
  
  .detail-item {
    .label {
      display: block;
      color: #ccc;
      font-size: 0.9rem;
      margin-bottom: 0.5rem;
    }
    
    .value {
      color: white;
      font-size: 1.1rem;
    }
  }
}

.action-container {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 2rem;
  margin-top: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 1rem;
  }
}

.action-buttons {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  height: 100%;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
    align-items: center;

    .action-btn {
      width: 100%;
      max-width: 300px;
      justify-content: center;
    }
  }
  
  .action-btn {
    padding: 0.8rem 1.5rem;
    border: none;
    border-radius: 4px;
    font-size: 1rem;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    height: fit-content;
    
    &.submit-another {
      background-color: #007bff;
      color: white;
      
      &:hover {
        background-color: #0056b3;
      }
    }
    
    &.ar-btn {
      background-color: #28a745;
      color: white;
      margin-bottom: 100px;
      &:hover {
        background-color: #218838;
      }

      .camera-icon {
        margin-right: 8px;
        font-size: 1.2rem;
      }
    }
  }
}

.qr-code-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  width: 200px;
  height: fit-content;
  
  .qr-code {
    width: 100%;
    height: auto;
    object-fit: contain;
  }
  
  .qr-instruction {
    color: white;
    font-size: 1rem;
    text-align: center;
    margin: 0;
  }
}
</style> 